import React, { useEffect, useMemo, useRef, useState } from "react";
import "./LandingPage.css";
import hero01 from "./assets/landing/hero-01.png";
import hero02 from "./assets/landing/hero-02.png";
import hero03 from "./assets/landing/hero-03.png";
import hero04 from "./assets/landing/hero-04.png";
import contactBriefingDesk from "./assets/landing/contact-briefing-desk.png";
import adamStepanekPhoto from "./assets/landing/about/adam-stepanek.jpg";
import ceskoPlusLogo from "./assets/landing/about/cesko-plus-logo.png";
import ipevsLogo from "./assets/landing/about/ipevs-logo.svg";
import { BRAND_COLORS, ElectionMapLogo } from "./branding";
import { sortElectionsNewestFirst } from "./electionOrdering";
import { RouterLink, useRouter } from "./router";

const PRICING_VERSION = "2026-03";

const ELECTION_GROUPS = [
  {
    key: "kv",
    label: "KV",
    description: "Komunální volby",
    defaultChecked: true,
    elections: ["kv2022", "kv2018", "kv2014"],
  },
  {
    key: "psp",
    label: "PSP",
    description: "Volby do PSP",
    defaultChecked: true,
    elections: ["psp2025", "psp2021", "psp2017"],
  },
  {
    key: "kraje",
    label: "Kraje",
    description: "Krajské volby",
    defaultChecked: false,
    elections: ["kraje2024"],
  },
  {
    key: "senat",
    label: "Senát",
    description: "Senátní volby",
    defaultChecked: false,
    elections: ["senat2020"],
  },
];

const previewSlides = [
  {
    label: "Srovnání",
    title: "Dvě volební mapy vedle sebe",
    image: hero01,
  },
  {
    label: "Trendy",
    title: "Vývoj okrsků v čase",
    image: hero02,
  },
  {
    label: "Detail",
    title: "Tabulka a mapa okrsků",
    image: hero03,
  },
  {
    label: "Přehled",
    title: "Přehled území",
    image: hero04,
  },
];

const STORY_SLIDE_DURATION_MS = 10000;

const problemCards = [
  {
    title: "Průzkum toho hodně napoví",
    text: "Bohužel, ale podá jen informaci o celkové náladě, preferencích či hlavních tématech.",
  },
  {
    title: "Základní volební data jsou fajn.",
    text: "Nicméně abyste je správně pochopili, tak je musíte vidět, a to ideálně nejen v prostoru, ale i v čase a napříč stranami.",
  },
  {
    title: "Procenta jsou důležitá",
    text: "Stejně tak podstatné jsou ale i absolutní počty hlasů, rozdíly ve volební účasti a počty voličů.",
  },
];

const clarityCards = [
  {
    title: "Stabilní okrsky",
    text: "Kde se dlouhodobě opakuje silná nebo slabá podpora.",
  },
  {
    title: "Měnící se okrsky",
    text: "Kde se volební chování posouvá napříč volbami.",
  },
  {
    title: "Růstový potenciál",
    text: "Kde může mít další práce kampaně největší smysl.",
  },
  {
    title: "Podobné chování",
    text: "Které části území hlasují podobně jako vaše cílová skupina.",
  },
];

const processSteps = [
  {
    title: "Vyplníte formulář",
    text: "Uvedete kontakt, území a typ voleb, které vás zajímají. Zabere to 2 minuty.",
  },
  {
    title: "Spojíme se s vámi",
    text: "Probereme detaily spolupráce a ujasníme si rozsah.",
  },
  {
    title: "Dostanete přístup + analýzu",
    text: "Vytvoříme vaší ElectionMap. Pošleme vám přístup a dostanete od nás úvodní analýzu celého vašeho volebního obvodu.",
  },
  {
    title: "Konzultace*",
    text: "Osobně nebo online se s vámi setkáme. Naučíme vás ElectionMap používat. Ukážeme vám konkrétní zajímavosti a zákonitosti vašeho obvodu.",
  },
];

const discoveryItems = [
  "Kde máte dlouhodobě nejsilnější podporu",
  "Kde existuje největší prostor pro růst",
  "Které okrsky hlasují podobně jako vaše cílová skupina",
  "Kde soupeříte o stejné voliče s jinými stranami",
  "Kde má smysl investovat energii kampaně",
  "Kde se volební chování dlouhodobě mění",
];

const faqItems = [
  {
    question: "Jaké volby jsou v ElectionMap k dispozici?",
    answer: "V rámci aplikace uvidíte poslední 3 volby do komunálu, PSP + případně i poslední volby do krajů (2024) a do Senátu (2020).",
  },
  {
    question: "Odkud ElectionMap bere data?",
    answer: "Z veřejných volebních výsledků a dostupných územních podkladů. Pracujeme s agregovanými daty.",
  },
  {
    question: "Pracujete s osobními daty voličů?",
    answer: "Ne. Veřejně komunikovaný produkt stojí na okrskových datech, ne na osobních profilech.",
  },
  {
    question: "Nahrazuje ElectionMap volební průzkum?",
    answer: "Ne. Průzkum čte aktuální náladu, ElectionMap doplňuje geografii historického hlasování.",
  },
];

function parseJsonSafe(response) {
  return response.json().catch(() => null);
}

function PreviewGallery({ compact = false }) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const progressRef = useRef(0);
  const startedAtRef = useRef(0);
  const activeSlide = previewSlides[activeIndex];

  function resetProgress() {
    progressRef.current = 0;
    startedAtRef.current = window.performance.now();
    setProgress(0);
  }

  function goToSlide(index) {
    setActiveIndex((index + previewSlides.length) % previewSlides.length);
    resetProgress();
  }

  function goToPreviousSlide() {
    setActiveIndex((current) => (current - 1 + previewSlides.length) % previewSlides.length);
    resetProgress();
  }

  function goToNextSlide() {
    setActiveIndex((current) => (current + 1) % previewSlides.length);
    resetProgress();
  }

  useEffect(() => {
    progressRef.current = progress;
  }, [progress]);

  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const updateMotionPreference = () => setPrefersReducedMotion(motionQuery.matches);
    updateMotionPreference();
    motionQuery.addEventListener("change", updateMotionPreference);
    return () => motionQuery.removeEventListener("change", updateMotionPreference);
  }, []);

  useEffect(() => {
    if (prefersReducedMotion || isPaused) return undefined;

    let animationFrameId = 0;

    function tick(timestamp) {
      if (!startedAtRef.current) {
        startedAtRef.current = timestamp - progressRef.current * STORY_SLIDE_DURATION_MS;
      }

      const elapsed = timestamp - startedAtRef.current;
      const nextProgress = Math.min(elapsed / STORY_SLIDE_DURATION_MS, 1);
      progressRef.current = nextProgress;
      setProgress(nextProgress);

      if (elapsed >= STORY_SLIDE_DURATION_MS) {
        startedAtRef.current = timestamp;
        progressRef.current = 0;
        setProgress(0);
        setActiveIndex((current) => (current + 1) % previewSlides.length);
      }

      animationFrameId = window.requestAnimationFrame(tick);
    }

    animationFrameId = window.requestAnimationFrame((timestamp) => {
      startedAtRef.current = timestamp - progressRef.current * STORY_SLIDE_DURATION_MS;
      tick(timestamp);
    });

    return () => window.cancelAnimationFrame(animationFrameId);
  }, [isPaused, prefersReducedMotion]);

  return (
    <figure
      className={`landing-preview${compact ? " landing-preview--compact" : ""}`}
      aria-label="Galerie screenshotů aplikace ElectionMap"
    >
      <div className="landing-preview__top">
        <div>
          <strong>{activeSlide.title}</strong>
          <span>Ukázka aplikace ElectionMap</span>
        </div>
        <button
          className="landing-preview__pause"
          type="button"
          aria-label={isPaused ? "Spustit galerii" : "Pozastavit galerii"}
          aria-pressed={isPaused}
          onClick={() => setIsPaused((current) => !current)}
        >
          {isPaused ? "Play" : "Pause"}
        </button>
      </div>
      <div className="landing-preview__progress" aria-label="Průběh galerie">
        {previewSlides.map((slide, index) => {
          const segmentProgress =
            index < activeIndex ? 1 : index === activeIndex ? progress : 0;
          return (
            <button
              key={slide.label}
              type="button"
              className="landing-preview__progress-segment"
              aria-label={`Zobrazit ukázku ${index + 1}: ${slide.title}`}
              aria-pressed={index === activeIndex}
              onClick={() => goToSlide(index)}
            >
              <span style={{ transform: `scaleX(${segmentProgress})` }} />
            </button>
          );
        })}
      </div>
      <div className="landing-preview__screen">
        <button
          className="landing-preview__arrow landing-preview__arrow--previous"
          type="button"
          aria-label="Předchozí ukázka"
          onClick={goToPreviousSlide}
        >
          ‹
        </button>
        <img src={activeSlide.image} alt={activeSlide.title} />
        <button
          className="landing-preview__arrow landing-preview__arrow--next"
          type="button"
          aria-label="Další ukázka"
          onClick={goToNextSlide}
        >
          ›
        </button>
      </div>
      <figcaption className="landing-preview__caption">
        {previewSlides.map((slide, index) => (
          <button
            key={slide.label}
            type="button"
            className={index === activeIndex ? "is-active" : ""}
            onClick={() => goToSlide(index)}
          >
            {slide.label}
          </button>
        ))}
      </figcaption>
    </figure>
  );
}

function RequestAccessForm() {
  const { navigate } = useRouter();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    requestedAreaText: "",
    note: "",
  });
  const [selectedGroups, setSelectedGroups] = useState(() =>
    ELECTION_GROUPS.filter((group) => group.defaultChecked).map((group) => group.key)
  );
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const selectedElectionIds = useMemo(
    () =>
      sortElectionsNewestFirst(
        ELECTION_GROUPS.filter((group) => selectedGroups.includes(group.key)).flatMap(
          (group) => group.elections
        )
      ),
    [selectedGroups]
  );

  function updateField(field, value) {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));
  }

  function toggleGroup(key) {
    setSelectedGroups((current) =>
      current.includes(key) ? current.filter((item) => item !== key) : [...current, key]
    );
  }

  async function onSubmit(event) {
    event.preventDefault();
    setError("");
    setIsSubmitting(true);

    try {
      const response = await fetch("/api/requests", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: form.email,
          name: form.name,
          phone: form.phone,
          requested_area_text: form.requestedAreaText,
          elections: selectedElectionIds,
          modules: [],
          note: form.note.trim() ? form.note.trim() : undefined,
          pricing_version: PRICING_VERSION,
          price_total_kc: null,
          price_breakdown: null,
        }),
      });
      const payload = await parseJsonSafe(response);

      if (!response.ok) {
        throw new Error(
          (payload && typeof payload.error === "string" && payload.error) || "Odeslání žádosti selhalo."
        );
      }

      try {
        const demoUrl = payload && payload.demo && typeof payload.demo.url === "string" ? payload.demo.url : "";
        if (demoUrl) window.sessionStorage.setItem("electionmap.demoLink", demoUrl);
      } catch (_) {
        // Demo link storage should never block request submission.
      }

      navigate("/thank-you", { replace: true });
    } catch (submitError) {
      setError(
        submitError && submitError.message
          ? String(submitError.message)
          : "Odeslání žádosti selhalo."
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <form className="landing-form" onSubmit={onSubmit}>
      <div className="landing-form__grid">
        <label>
          <span>Jméno</span>
          <input
            required
            autoComplete="name"
            value={form.name}
            onChange={(event) => updateField("name", event.target.value)}
          />
        </label>
        <label>
          <span>E-mail</span>
          <input
            type="email"
            required
            autoComplete="email"
            value={form.email}
            onChange={(event) => updateField("email", event.target.value)}
          />
        </label>
        <label>
          <span>Telefon</span>
          <input
            type="tel"
            required
            autoComplete="tel"
            value={form.phone}
            onChange={(event) => updateField("phone", event.target.value)}
          />
        </label>
        <label>
          <span>Území / volební obvod</span>
          <input
            required
            value={form.requestedAreaText}
            onChange={(event) => updateField("requestedAreaText", event.target.value)}
            placeholder="např. Praha 10, Teplice"
          />
        </label>
      </div>

      <fieldset className="landing-form__fieldset">
        <legend>Historická data z voleb</legend>
        <div className="landing-form__checks">
          {ELECTION_GROUPS.map((group) => (
            <label key={group.key} className="landing-check">
              <input
                type="checkbox"
                checked={selectedGroups.includes(group.key)}
                onChange={() => toggleGroup(group.key)}
              />
              <span>
                <strong>{group.label}</strong>
                <small>{group.description}</small>
              </span>
            </label>
          ))}
        </div>
      </fieldset>

      <label className="landing-form__note">
        <span>Poznámka</span>
        <textarea
          rows={4}
          value={form.note}
          onChange={(event) => updateField("note", event.target.value)}
          placeholder="Pokud chcete, tak krátce napište další informace o vás, vaší situaci a vaší plánované kampani - např. velikost vašeho obvodu, zkušenosti s kampaní, případně jaká data máte nyní k dispozici nebo co dalšího vás zajímá. Více pak probereme při úvodní konzultaci po telefonu."
        />
      </label>

      {error ? <div className="landing-form__error">{error}</div> : null}

      <button className="landing-button landing-button--form" type="submit" disabled={isSubmitting}>
        {isSubmitting ? "Odesílání..." : "Zavolejte mi"}
      </button>
      <p className="landing-form__privacy-note">
        Ozveme se vám telefonicky do 24 hodin. Kontaktní údaje použijeme pouze pro kontakt ohledně
        ElectionMap.
      </p>
    </form>
  );
}

export default function LandingPage({ autoScrollToForm = false }) {
  const formSectionRef = useRef(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    if (!autoScrollToForm) return undefined;

    const frame = window.requestAnimationFrame(() => {
      if (!formSectionRef.current) return;
      formSectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      const firstInput = formSectionRef.current.querySelector("input");
      if (firstInput) firstInput.focus({ preventScroll: true });
    });

    return () => window.cancelAnimationFrame(frame);
  }, [autoScrollToForm]);

  return (
    <main
      className="landing-page"
      style={{
        "--brand-dark-blue": BRAND_COLORS.primaryDarkBlue,
        "--brand-bright-blue": BRAND_COLORS.primaryBrightBlue,
        "--brand-red": BRAND_COLORS.accentRed,
      }}
    >
      <header className="landing-header">
        <RouterLink to="/" className="landing-header__brand" aria-label="ElectionMap home">
          <ElectionMapLogo height={46} />
        </RouterLink>
        <nav className="landing-header__nav" aria-label="Hlavní navigace">
          <RouterLink to="/app/login">Přihlášení</RouterLink>
        </nav>
        <a href="#request-access" className="landing-button landing-button--outline landing-header__cta">
          Chci ElectionMap.
        </a>
        <button
          className="landing-header__menu-button"
          type="button"
          aria-label={isMobileMenuOpen ? "Zavřít menu" : "Otevřít menu"}
          aria-expanded={isMobileMenuOpen}
          aria-controls="landing-mobile-menu"
          onClick={() => setIsMobileMenuOpen((current) => !current)}
        >
          <span />
          <span />
          <span />
        </button>
        <div
          className={`landing-header__mobile-menu${isMobileMenuOpen ? " is-open" : ""}`}
          id="landing-mobile-menu"
        >
          <RouterLink to="/app/login" onClick={() => setIsMobileMenuOpen(false)}>
            Přihlášení
          </RouterLink>
          <a href="#request-access" onClick={() => setIsMobileMenuOpen(false)}>
            Chci ElectionMap.
          </a>
        </div>
      </header>

      <section className="landing-hero" aria-labelledby="landing-hero-title">
        <div className="landing-hero__inner">
          <div className="landing-hero__copy">
            <p className="landing-hero__setting">
              „Žádná část vzdělání politika není nepostradatelnější než boj ve volbách.“
              <br />~ Winston Churchill
            </p>
            <h1 id="landing-hero-title">
              <span className="landing-hero__line">
                Sledujte <span className="landing-hero__accent">okrsky</span>.
              </span>
              <span className="landing-hero__line">
                V <span className="landing-hero__accent">mapě</span>, detailně
                <span className="landing-hero__red-dot">.</span>
              </span>
            </h1>
            <p className="landing-hero__subheadline">
              Data mají všichni - udělejte z nich čitelný obraz celého území. Pochopte ho a celý váš
              volební obvod konečně začne dávat smysl. ElectionMap vám odhalí celý jeho příběh
              včetně historie, souvislostí a pravidel hry.
            </p>
            <div className="landing-hero__actions">
              <a href="#request-access" className="landing-button landing-button--light">
                Chci ElectionMap.
              </a>
              <a href="#ukazky" className="landing-button landing-button--ghost">
                Prohlédnout ukázky
              </a>
            </div>
          </div>
          <div className="landing-hero__visual">
            <PreviewGallery compact />
          </div>
        </div>
      </section>

      <section className="landing-trust-bar" aria-label="Důvěryhodnost dat">
        <div>
          <strong>Data z oficiálních zdrojů:</strong>
          <span>ČSÚ • Ministerstvo vnitra • veřejné volební výsledky</span>
        </div>
        <div>
          <span>Bez osobních dat voličů</span>
          <span>Agregovaná okrsková data</span>
          <span>Analýza + konzultace</span>
        </div>
      </section>

      <section className="landing-section" id="problem">
        <div className="landing-section__intro landing-section__intro--center">
          <p className="landing-section__label">Problém, který řešíme</p>
          <h2>Souhrnná obecná data dnes už nestačí.</h2>
        </div>
        <div className="landing-card-grid landing-card-grid--three">
          {problemCards.map((item) => (
            <article key={item.title} className="landing-card">
              <h3>{item.title}</h3>
              <p>{item.text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="landing-section landing-showcase" id="ukazky">
        <div className="landing-section__intro">
          <p className="landing-section__label">Ukázky z živého webu</p>
          <h2>Skutečné příklady z aplikace</h2>
          <p>
            Kromě komplexních volebních dat uvidíte dlouhodobé trendy, rozpoznáte anomálie a
            pochopíte souvislosti v prostoru i v čase.
          </p>
        </div>
        <PreviewGallery />
      </section>

      <section className="landing-section landing-section--blue-band" id="citelnejsi">
        <div className="landing-section__split">
          <div className="landing-section__intro">
            <p className="landing-section__label">ElectionMap</p>
            <h2>Co začne být čitelnější</h2>
            <p>
              Mapa nedává jen další vrstvu dat. Ukáže vzorce, které v tabulkách zanikají, a pomůže
              převést je do rozhodnutí pro kampaň.
            </p>
          </div>
          <div className="landing-card-grid landing-card-grid--two">
            {clarityCards.map((item) => (
              <article key={item.title} className="landing-card landing-card--soft">
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="landing-section landing-process-section" id="jak-funguje">
        <div className="landing-section__intro landing-section__intro--center">
          <p className="landing-section__label">Jednoduchý proces</p>
          <h2>Jak vypadá spolupráce s ElectionMap v praxi</h2>
        </div>
        <ol className="landing-process">
          {processSteps.map((step, index) => (
            <li key={step.title}>
              <span>{String(index + 1).padStart(2, "0")}</span>
              <h3>{step.title}</h3>
              <p>{step.text}</p>
            </li>
          ))}
        </ol>
        <p className="landing-process-note">
          *) Do ElectionMap lze po domluvě zanést a promítnout i další vaše informace a data, která
          máte k dispozici - umístění billboardů z minulé kampaně, nasbírané podněty a průzkumy nebo
          jiná otevřená data.
        </p>
      </section>

      <section className="landing-section landing-discovery" id="objevy">
        <div className="landing-section__intro">
          <p className="landing-section__label">Praktické otázky</p>
          <h2>Co konkrétně díky ElectionMap objevíte</h2>
        </div>
        <div className="landing-discovery__grid">
          {discoveryItems.map((item) => (
            <p key={item}>{item}</p>
          ))}
        </div>
      </section>

      <section className="landing-section landing-cta">
        <div>
          <p className="landing-section__label">Další krok</p>
          <h2>Zjistěte, co by mapa ukázala ve vašem území.</h2>
          <p>Cena a rozsah spolupráce individuálně podle území a typu voleb.</p>
        </div>
        <a href="#request-access" className="landing-button landing-button--light">
          Chci ElectionMap.
        </a>
      </section>

      <section className="landing-section landing-request" id="request-access" ref={formSectionRef}>
        <div className="landing-section__intro">
          <p className="landing-section__label">Kontakt</p>
          <h2>
            Chci{" "}
            <span className="landing-request__brand-election">Election</span>
            <span className="landing-request__brand-map">Map</span>
            <span className="landing-request__red-dot">.</span>
          </h2>
          <p>
            Vyplňte základní údaje. Ozveme se, upřesníme území a navrhneme smysluplný rozsah
            spolupráce.
          </p>
          <figure className="landing-request__photo">
            <img
              src={contactBriefingDesk}
              alt="Analytický briefing ElectionMap před úvodní konzultací"
            />
          </figure>
        </div>
        <RequestAccessForm />
      </section>

      <section className="landing-section landing-faq" id="faq">
        <div className="landing-section__intro landing-section__intro--center">
          <p className="landing-section__label">FAQ</p>
          <h2>Časté otázky</h2>
        </div>
        <div className="landing-faq__grid">
          {faqItems.map((item) => (
            <details key={item.question} className="landing-faq__item">
              <summary>
                <span>{item.question}</span>
                <span aria-hidden="true">⌄</span>
              </summary>
              <p>{item.answer}</p>
            </details>
          ))}
        </div>
      </section>

      <section className="landing-section landing-about" id="o-projektu">
        <div className="landing-section__intro">
          <p className="landing-section__label">O projektu</p>
          <h2>Proč jsme vytvořili ElectionMap.</h2>
          <p>
            ElectionMap. vznikla z přesvědčení, že dobrá politická rozhodnutí mají vycházet z dat a
            skutečného porozumění území. Cílem projektu je pomáhat politikům, zastupitelům a
            volebním týmům lépe chápat své obce, města a voliče.
          </p>
        </div>
        <div className="landing-about__grid">
          <article className="landing-about__founder" aria-label="Founder ElectionMap">
            <figure className="landing-about__photo">
              <img src={adamStepanekPhoto} alt="Adam Štěpánek" />
            </figure>
            <div>
              <h3>Adam Štěpánek</h3>
              <div className="landing-about__roles">
                <p>Founder</p>
              </div>
              <ul className="landing-about__bio">
                <li>člen správní rady IPEVS, z. ú.</li>
                <li>
                  <span>člen</span>
                  <img className="landing-about__cesko-logo" src={ceskoPlusLogo} alt="Česko+" />
                </li>
                <li>ICT-sector business developer</li>
                <li>Zastupitel MČ P10</li>
              </ul>
            </div>
            <blockquote className="landing-about__quote">
              "Ideální je, když se politika dělá na základě dat, nikoli pocitů. Proto jsme
              vytvořili ElectionMap."
            </blockquote>
          </article>
          <aside className="landing-about__partner" aria-label="Partner projektu">
            <div className="landing-about__partner-logo-frame">
              <img className="landing-about__partner-logo" src={ipevsLogo} alt="Institut Efektivity" />
            </div>
            <div className="landing-about__partner-heading">
              <span className="landing-about__partner-label">Ve spolupráci s</span>
              <strong>Institut Efektivity</strong>
              <span>IPEVS, z. ú.</span>
            </div>
            <blockquote className="landing-about__partner-quote">
              Institut Efektivity pomáhá veřejným institucím, samosprávám i organizacím hledat
              praktičtější, srozumitelnější a lépe měřitelné způsoby práce.
            </blockquote>
          </aside>
        </div>
      </section>

      <footer className="landing-footer">
        <div className="landing-footer__inner">
          <div className="landing-footer__brand">
            <ElectionMapLogo height={42} style={{ "--logo-visible-offset": "-8.8px" }} />
            <p>Analytická a konzultační služba nad volební geografií.</p>
          </div>
          <address className="landing-footer__operator">
            <strong>Provozovatel portálu: PIAF, a.s.</strong>
            <span>V přístavu 1585/8</span>
            <span>Praha 7, 170 00</span>
            <span>IČ: 29024978</span>
          </address>
          <nav className="landing-footer__nav" aria-label="Patička webu">
            <a href="#gdpr" aria-disabled="true">
              GDPR
            </a>
            <a href="#smluvni-podminky" aria-disabled="true">
              Smluvní podmínky
            </a>
            <a href="#request-access">Kontakt</a>
            <RouterLink to="/app/login">Přístup do aplikace</RouterLink>
          </nav>
        </div>
        <div className="landing-footer__bottom">
          <span className="landing-footer__made">
            Vyvinuto s <span className="landing-footer__heart">♥</span> v Praze
          </span>
          <span className="landing-footer__copyright">© 2026 ElectionMap.</span>
        </div>
      </footer>
    </main>
  );
}
